import { Button, Stack } from '@mui/material';
import { CleaningMission } from '@silbo/core';
import { colors } from '@silbo/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticationContext } from '../../../contexts/AuthenticationContext';
import { useConfigContext } from '../../../contexts/ConfigContext/useConfigContext';
import { useCleaningMissionGestures } from '../hooks/useCleaningMissionGestures';
import { canInterruptOrFinish, canStart, canReserve, canCancelReservation } from '../utils/cleaning-mission-gestures';
import { congratulate } from '../utils/congratulate';
import InterruptCleaningMissionDialog from './InterruptCleaningMissionDialog';

type CleaningMissionGesturesProps = {
  mission: CleaningMission;
  onFinished: () => void;
};

const BUTTON_SX = { height: 52, borderRadius: '12px', fontSize: 16, fontWeight: 700 } as const;
const SECONDARY_SX = { ...BUTTON_SX, flex: 1, borderWidth: '1.5px', '&:hover': { borderWidth: '1.5px' } } as const;
const PRIMARY_SX = { ...BUTTON_SX, flex: 1.4 } as const;

const CleaningMissionGestures = ({ mission, onFinished }: CleaningMissionGesturesProps) => {
  const { t } = useTranslation('environmental-cleaning');
  const { user } = useAuthenticationContext();
  const { config } = useConfigContext();
  const { start, interrupt, finish, reserve, cancelReservation, isPending } = useCleaningMissionGestures(mission.id);
  const [isInterruptOpen, setInterruptOpen] = useState(false);

  const viewer = { userId: user?.id, canCleanBeds: !!config?.cleanBeds };
  const showsReserve = canReserve(mission, viewer);
  const showsCancelReservation = canCancelReservation(mission, viewer);
  const showsStart = canStart(mission, viewer);
  const showsInterruptOrFinish = canInterruptOrFinish(mission, viewer);

  if (!showsReserve && !showsCancelReservation && !showsStart && !showsInterruptOrFinish) {
    return null;
  }
  return (
    <Stack
      direction="row"
      gap="10px"
      sx={{
        flex: 'none',
        paddingTop: 3,
        paddingX: 4,
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)',
        borderTop: `1px solid ${colors.grey[10]}`,
        bgcolor: 'common.white',
      }}
    >
      {showsReserve && (
        <Button variant="outlined" onClick={() => reserve()} disabled={isPending} sx={SECONDARY_SX}>
          {t('actions.reserve')}
        </Button>
      )}
      {showsCancelReservation && (
        <Button variant="outlined" onClick={() => cancelReservation()} disabled={isPending} sx={SECONDARY_SX}>
          {t('actions.cancelReservationShort')}
        </Button>
      )}
      {showsStart && (
        <Button onClick={() => start()} disabled={isPending} sx={PRIMARY_SX}>
          {t('actions.start')}
        </Button>
      )}
      {showsInterruptOrFinish && (
        <>
          <Button
            variant="outlined"
            onClick={() => setInterruptOpen(true)}
            disabled={isPending}
            sx={{
              ...SECONDARY_SX,
              borderColor: colors.red[80],
              color: colors.red[90],
              '&:hover': SECONDARY_SX['&:hover'],
            }}
          >
            {t('actions.interrupt')}
          </Button>
          <Button
            onClick={() =>
              finish(undefined, {
                onSuccess: () => {
                  onFinished();
                  congratulate(t, mission);
                },
              })
            }
            disabled={isPending}
            sx={PRIMARY_SX}
          >
            {t('actions.finish')}
          </Button>
        </>
      )}
      {isInterruptOpen && (
        <InterruptCleaningMissionDialog
          open
          isPending={isPending}
          onClose={() => setInterruptOpen(false)}
          onConfirm={interrupt}
        />
      )}
    </Stack>
  );
};

export default CleaningMissionGestures;
